import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  Mail,
  Phone,
  Plus,
  Search,
  UserRoundPlus,
  X,
} from "lucide-react";

import { classifyVisitor, searchVisitors } from "@/api/visitors";
import { Button } from "@/components/ui/button";
import type {
  CreateVisitorPayload,
  VisitorSearchResult,
} from "@/types/visitor";

type Props = {
  open: boolean;
  churchId: string;
  serviceOccurrenceId: string;

  isSubmitting: boolean;
  errorMessage?: string | null;

  onClose: () => void;

  onCheckInExisting: (
    visitorId: string
  ) => Promise<void>;

  onCreateVisitor: (
    payload: CreateVisitorPayload
  ) => Promise<void>;
};

export function VisitorCheckInDialog(props: Props) {
  return props.open ? <VisitorCheckInDialogContent key={`${props.churchId}:${props.serviceOccurrenceId}`} {...props} /> : null;
}

function VisitorCheckInDialogContent({
  open,
  churchId,
  serviceOccurrenceId,
  isSubmitting,
  errorMessage,
  onClose,
  onCheckInExisting,
  onCreateVisitor,
}: Props) {
  const [classification, setClassification] = useState<"" | "REGULAR" | "YOUTH">("");
  const [classifyMode, setClassifyMode] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [search, setSearch] = useState("");
  const [showCreateForm, setShowCreateForm] =
    useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");

  const [validationError, setValidationError] =
    useState<string | null>(null);

  const searchQuery = useQuery({
    queryKey: [
      "visitor-search",
      classifyMode,
      churchId,
      serviceOccurrenceId,
      search,
    ],
    enabled:
      open &&
      !showCreateForm &&
      search.trim().length >= 2,
    queryFn: () =>
      searchVisitors(
        churchId,
        search.trim(),
        classifyMode ? undefined : serviceOccurrenceId
      ),
  });

  if (!open) {
    return null;
  }

  async function handleCreateVisitor(
    event: React.FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();
    setValidationError(null);

    if (!firstName.trim()) {
      setValidationError(
        "First name is required."
      );

      return;
    }

    if (!lastName.trim()) {
      setValidationError(
        "Last name is required."
      );

      return;
    }

    if (!classification) { setValidationError("Choose Youth or Regular attendance."); return; }
    await onCreateVisitor({
      attendance_classification: classification,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      phone: phone.trim() || null,
      email: email.trim() || null,
      notes: notes.trim() || null,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/80 px-4 py-8 backdrop-blur-sm">
      <div className="w-full max-w-2xl overflow-hidden rounded-3xl border border-slate-700 bg-slate-900 shadow-2xl shadow-black/50">
        <div className="relative border-b border-slate-800 px-7 py-6">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-sky-500 via-indigo-500 to-violet-500" />

          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-500/15 text-sky-400">
                <UserRoundPlus size={22} />
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-white">
                  Visitor Check-In
                </h2>

                <p className="mt-1 text-sm text-slate-400">
                  Find a returning visitor or add a new guest.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-800 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {!showCreateForm ? (
          <div className="p-7">
            <label className="mb-3 block text-sm text-slate-300"><input type="checkbox" checked={classifyMode} onChange={e => setClassifyMode(e.target.checked)} /> Classify an existing visitor (authorized staff)</label>
            {validationError && <p role="alert" className="mb-3 text-rose-300">{validationError}</p>}
            {searchQuery.isError && <p role="alert" className="mb-3 text-rose-300">Unable to search visitors in this context.</p>}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />

              <input
                value={search}
                onChange={(event) =>
                  setSearch(event.target.value)
                }
                placeholder="Search phone, email, or visitor name..."
                className="h-13 w-full rounded-xl border border-slate-700 bg-slate-950 py-3 pl-12 pr-4 text-white placeholder:text-slate-600 focus:border-sky-500 focus:outline-none"
                autoFocus
              />
            </div>

            <div className="mt-5 max-h-[380px] overflow-y-auto">
              {search.trim().length < 2 ? (
                <VisitorSearchPrompt />
              ) : searchQuery.isLoading ? (
                <div className="py-10 text-center text-sm text-slate-400">
                  Searching visitors...
                </div>
              ) : searchQuery.data?.length ? (
                <div className="space-y-3">
                  {searchQuery.data.map(
                    (visitor) => (
                      classifyMode ? <div key={visitor.id} className="rounded border border-slate-700 p-3 text-slate-300">
                        <p>{visitor.first_name} {visitor.last_name}</p>
                        {visitor.attendance_classification ? <p>{visitor.attendance_classification}</p> : <div className="mt-2 flex gap-3">
                          {(["REGULAR", "YOUTH"] as const).map(value => <button key={value} type="button" disabled={classifying} className="rounded bg-slate-800 p-2" onClick={async () => {
                            setClassifying(true); setValidationError(null);
                            try { await classifyVisitor(churchId, visitor.id, value); await searchQuery.refetch(); }
                            catch { setValidationError("Unable to classify visitor. Check your permissions."); }
                            finally { setClassifying(false); }
                          }}>Classify as {value === "YOUTH" ? "Youth" : "Regular"}</button>)}
                        </div>}
                      </div> : <VisitorSearchRow
                        key={visitor.id}
                        visitor={visitor}
                        isSubmitting={isSubmitting}
                        onCheckIn={
                          onCheckInExisting
                        }
                      />
                    )
                  )}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-8 text-center">
                  <p className="font-medium text-white">
                    No existing visitor found
                  </p>

                  <p className="mt-2 text-sm text-slate-400">
                    Create a visitor profile and check them in.
                  </p>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() =>
                setShowCreateForm(true)
              }
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl border border-sky-500/20 bg-sky-500/10 px-5 py-3 font-semibold text-sky-300 transition hover:bg-sky-500/15"
            >
              <Plus size={18} />
              Add New Visitor
            </button>

            {errorMessage && (
              <div className="mt-5 rounded-xl border border-rose-500/20 bg-rose-500/5 px-4 py-3 text-sm text-rose-300">
                {errorMessage}
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleCreateVisitor}>
            <label className="m-6 block text-sm text-slate-300">Attendance designation *
              <select required className="mt-2 w-full rounded border border-slate-700 bg-slate-950 p-3" value={classification} onChange={e => setClassification(e.target.value as typeof classification)}>
                <option value="">Choose designation</option><option value="REGULAR">Main Church</option><option value="YOUTH">Youth Ministry</option>
              </select>
            </label>
            <div className="grid gap-5 p-7 sm:grid-cols-2">
              <FormField label="First Name">
                <input
                  value={firstName}
                  onChange={(event) =>
                    setFirstName(
                      event.target.value
                    )
                  }
                  className={inputClassName}
                  autoFocus
                />
              </FormField>

              <FormField label="Last Name">
                <input
                  value={lastName}
                  onChange={(event) =>
                    setLastName(
                      event.target.value
                    )
                  }
                  className={inputClassName}
                />
              </FormField>

              <FormField label="Phone">
                <input
                  value={phone}
                  onChange={(event) =>
                    setPhone(event.target.value)
                  }
                  className={inputClassName}
                />
              </FormField>

              <FormField label="Email">
                <input
                  type="email"
                  value={email}
                  onChange={(event) =>
                    setEmail(event.target.value)
                  }
                  className={inputClassName}
                />
              </FormField>

              <FormField
                label="Notes"
                className="sm:col-span-2"
              >
                <textarea
                  value={notes}
                  onChange={(event) =>
                    setNotes(event.target.value)
                  }
                  rows={3}
                  className={`${inputClassName} h-auto py-3`}
                />
              </FormField>

              {(validationError ||
                errorMessage) && (
                <div className="sm:col-span-2 rounded-xl border border-rose-500/20 bg-rose-500/5 px-4 py-3 text-sm text-rose-300">
                  {validationError ||
                    errorMessage}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-800 px-7 py-5">
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  setShowCreateForm(false)
                }
                disabled={isSubmitting}
              >
                Back
              </Button>

              <Button
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Checking In..."
                  : "Create & Check In"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function VisitorSearchRow({
  visitor,
  isSubmitting,
  onCheckIn,
}: {
  visitor: VisitorSearchResult;
  isSubmitting: boolean;
  onCheckIn: (
    visitorId: string
  ) => Promise<void>;
}) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sky-500/15 font-bold text-sky-300">
        {visitor.first_name[0]}
        {visitor.last_name[0]}
      </div>

      <div className="min-w-0 flex-1">
        <p className="font-semibold text-white">
          {visitor.first_name}{" "}
          {visitor.last_name}
        </p>

        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          {visitor.phone && (
            <span className="flex items-center gap-1">
              <Phone size={12} />
              {visitor.phone}
            </span>
          )}

          {visitor.email && (
            <span className="flex items-center gap-1">
              <Mail size={12} />
              {visitor.email}
            </span>
          )}
        </div>

        <p className="mt-2 flex items-center gap-1 text-xs text-slate-500">
          <Clock3 size={12} />

          {visitor.visit_count === null ? "Visit history unavailable" : visitor.visit_count === 0
            ? "No prior visits"
            : `${visitor.visit_count} previous visit${
                visitor.visit_count === 1
                  ? ""
                  : "s"
              }`}
        </p>
      </div>

      {visitor.is_checked_in ? (
        <span className="flex items-center gap-2 rounded-xl bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-300">
          <CheckCircle2 size={16} />
          Checked In
        </span>
      ) : (
        <button
          type="button"
          disabled={isSubmitting}
          onClick={() =>
            onCheckIn(visitor.id)
          }
          className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:-translate-y-0.5 disabled:opacity-60"
        >
          Check In
          <ArrowRight size={15} />
        </button>
      )}
    </div>
  );
}

function VisitorSearchPrompt() {
  return (
    <div className="py-10 text-center">
      <Search
        className="mx-auto text-slate-600"
        size={30}
      />

      <p className="mt-4 font-medium text-white">
        Find a returning visitor
      </p>

      <p className="mt-2 text-sm text-slate-400">
        Search by phone, email, or name.
      </p>
    </div>
  );
}

function FormField({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={className}>
      <span className="text-sm font-medium text-slate-300">
        {label}
      </span>

      <span className="mt-2 block">
        {children}
      </span>
    </label>
  );
}

const inputClassName =
  "w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-sky-500 focus:outline-none";